import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { biomarkerDefs, seedBiomarkerRecords, type BiomarkerRecord } from '../data/biomarkers';
interface NewRecord {
  biomarkerId: string;
  value: number;
  unit: string;
  date: string;
  source?: string;
}
interface BiomarkersValue {
  recordsFor: (biomarkerId: string) => BiomarkerRecord[];
  addRecord: (record: NewRecord) => void;
  /** Display unit preference per biomarker. */
  unitFor: (biomarkerId: string) => string;
  setUnit: (biomarkerId: string, unit: string) => void;
  /** Per-biomarker public flag. PRIVATE by default. */
  isPublic: (biomarkerId: string) => boolean;
  setPublic: (biomarkerId: string, value: boolean) => void;
}
const BiomarkersContext = createContext<BiomarkersValue | null>(null);

/**
 * Prototype store for lab results.
 * REQUIRES BACKEND: storage, access control and audit of health data.
 */
export function BiomarkersProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [records, setRecords] = useState<BiomarkerRecord[]>(seedBiomarkerRecords);
  const [units, setUnits] = useState<Record<string, string>>(() => biomarkerDefs.reduce<Record<string, string>>((acc, def) => {
    acc[def.id] = def.canonicalUnit;
    return acc;
  }, {}));
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const addRecord = useCallback((record: NewRecord) => {
    const def = biomarkerDefs.find(item => item.id === record.biomarkerId);
    if (!def) return;
    const canonical = record.value / (def.factors[record.unit] ?? 1);
    setRecords(prev => [{
      id: `bm-${Date.now()}`,
      biomarkerId: record.biomarkerId,
      value: canonical,
      enteredUnit: record.unit,
      date: record.date,
      source: record.source
    }, ...prev]);
  }, []);
  const value = useMemo<BiomarkersValue>(() => ({
    recordsFor: biomarkerId => records.filter(record => record.biomarkerId === biomarkerId).sort((a, b) => b.date.localeCompare(a.date)),
    addRecord,
    unitFor: biomarkerId => units[biomarkerId],
    setUnit: (biomarkerId, unit) => setUnits(prev => ({
      ...prev,
      [biomarkerId]: unit
    })),
    isPublic: biomarkerId => Boolean(visibility[biomarkerId]),
    setPublic: (biomarkerId, next) => setVisibility(prev => ({
      ...prev,
      [biomarkerId]: next
    }))
  }), [records, units, visibility, addRecord]);
  return <BiomarkersContext.Provider value={value} data-id="element-1516" data-mp="xujmmjka6uw">{children}</BiomarkersContext.Provider>;
}
export function useBiomarkers(): BiomarkersValue {
  const ctx = useContext(BiomarkersContext);
  if (!ctx) throw new Error('useBiomarkers must be used inside BiomarkersProvider');
  return ctx;
}